/**
 * The cover, or an honest stand-in for one.
 *
 * A thumbnail is the fastest thing on the screen to recognise — faster than
 * the title, which is why every list here leads with one. But the catalogues
 * do not always have an image: Hardcover's series documents carry none at
 * all, and a broken picture icon is worse than no picture. So the fallback is
 * a drawn mark for what the thing *is*, which at least tells you whether you
 * are looking at a book, a film or a person.
 */

import { useState } from 'react';
import type { Medium, WatchableKind } from '../../poller/types.js';

interface Props {
  src: string | null;
  alt?: string;
  kind: WatchableKind;
  medium: Medium;
  size?: 'sm' | 'md' | 'lg';
}

export function Thumb({ src, alt = '', kind, medium, size = 'md' }: Props) {
  const className = `thumb thumb-${size}`;
  // Covers go missing: the catalogue keeps the URL after the asset behind it
  // has gone. Falling back to the drawn mark keeps the row the same shape as
  // every other row, which a broken-image glyph would not.
  const [broken, setBroken] = useState(false);

  if (src && !broken) {
    return (
      <img
        className={className}
        src={src}
        alt={alt}
        loading="lazy"
        decoding="async"
        onError={() => setBroken(true)}
      />
    );
  }

  return (
    <span className={`${className} thumb-empty`} aria-hidden="true">
      <Mark kind={kind} medium={medium} />
    </span>
  );
}

function Mark({ kind, medium }: { kind: WatchableKind; medium: Medium }) {
  const common = {
    width: 18,
    height: 18,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  if (kind === 'creator') {
    return (
      <svg {...common}>
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c0-3.6 3.1-6 7-6s7 2.4 7 6" />
      </svg>
    );
  }

  if (medium === 'book') {
    return (
      <svg {...common}>
        <path d="M5 4h9a3 3 0 0 1 3 3v13H8a3 3 0 0 1-3-3z" />
        <path d="M8 20a3 3 0 0 1 3-3h6" />
      </svg>
    );
  }

  if (medium === 'tv') {
    return (
      <svg {...common}>
        <rect x="3" y="7" width="18" height="12" rx="2" />
        <path d="M8 3l4 4 4-4" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M8 4v16M16 4v16M3 12h18" />
    </svg>
  );
}
